import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { Navigate, useParams } from "react-router-dom";

import { ArticleEditForm } from "@/components/ui/admin/articles/ArticleEditForm";
import { createArticleService, createTaxonomyService, createTeamService } from "@/core/services/factory";
import {
  getArticleSharePath,
  getAssignableTeamMembers,
  isCancellableArticleDraft,
} from "@/core/services/types/article.types";
import { getLibraryReturnHref } from "@/lib/library-search-snapshot";
import { useTranslations } from "@/lib/useTranslations";

export function ArticleEditPage() {
  const { id } = useParams<{ id: string }>();
  const t = useTranslations("articles.edit");

  const { data, isLoading, isError } = useQuery({
    queryKey: ["article-edit", id],
    enabled: Boolean(id),
    placeholderData: keepPreviousData,
    queryFn: async () => {
      if (!id) throw new Error("missing id");
      const articles = createArticleService();
      const team = createTeamService();
      const taxonomy = createTaxonomyService();
      const [article, teamMembers, suggestions, [articleTaxonomy, taxonomyData]] =
        await Promise.all([
          articles.getForEdit(id),
          team.listAll(),
          articles.getSuggestions(id),
          Promise.all([articles.getArticleTaxonomy(id), taxonomy.listAll()]),
        ]);
      return { article, teamMembers, suggestions, articleTaxonomy, taxonomyData };
    },
  });

  if (!id) return <Navigate to={getLibraryReturnHref("articles")} replace />;
  if (isLoading && !data) {
    return (
      <div className="mx-auto flex w-full max-w-6xl flex-1 items-center justify-center px-6 py-10 text-sm text-neutral-500">
        Chargement…
      </div>
    );
  }
  if (isError || !data?.article) {
    return <Navigate to={getLibraryReturnHref("articles")} replace />;
  }

  const { article, teamMembers, suggestions, articleTaxonomy, taxonomyData } = data;
  const assignableMembers = getAssignableTeamMembers(
    teamMembers,
    article.assigned_to,
  );
  const adminOrigin = import.meta.env.VITE_ADMIN_URL?.replace(/\/$/, "");
  const path = getArticleSharePath(id);
  const shareUrl = adminOrigin ? `${adminOrigin}${path}` : path;

  return (
    <div className="mx-auto flex w-full max-w-6xl flex-1 flex-col gap-8 overflow-y-auto px-6 py-10">
      <p className="text-sm font-medium uppercase tracking-widest text-neutral-500">
        {t("eyebrow")}
      </p>

      <ArticleEditForm
        key={article.id}
        article={article}
        assignableMembers={assignableMembers}
        shareUrl={shareUrl}
        canCancelCreation={isCancellableArticleDraft(article)}
        suggestions={suggestions}
        taxonomy={articleTaxonomy}
        artForms={taxonomyData.artForms}
        artisticPeriods={taxonomyData.artisticPeriods}
        culturalAreas={taxonomyData.culturalAreas}
        exhibitionTypes={taxonomyData.exhibitionTypes}
      />
    </div>
  );
}
